import { Switch } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { useDataTableContext } from "@msuite/ui/table";
import { guardArray, uniqueArray } from "@msuite/utils/array";
import type { SubstitutionPlanContactNotificationListSchema } from "@msuite/utils/schemas";
import { generateId } from "@msuite/utils/strings";
import type { ContactMember } from "@msuite/utils/types";
import { useFormContext, useWatch } from "react-hook-form";
import { useSubstitutionPlanContactModalContext } from "@/modals/users/substitution-plan-contact-modal/context";

/** Props Interface */
interface SubstitutionPlanMemberContactNotificationSwitchProps {
	contactMember: ContactMember;
}

export function SubstitutionPlanMemberContactNotificationSwitch({
	contactMember,
}: SubstitutionPlanMemberContactNotificationSwitchProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** State */
	const { getSelectedRowIds } = useDataTableContext();
	const { substitutionPlanProjectItemId } = useSubstitutionPlanContactModalContext();
	const methods = useFormContext<SubstitutionPlanContactNotificationListSchema>();
	const [currentSelection] = useWatch({ control: methods.control, name: ["items"] });

	const isChecked = guardArray(currentSelection).some(
		(item) => item.contactMemberId === contactMember.id,
	);

	/** Functions */
	function handleOnCheckedChange() {
		const currentSelection = methods.getValues("items");
		const isChecked = guardArray(currentSelection).some(
			(item) => item.contactMemberId === contactMember.id,
		);
		const ids = uniqueArray(getSelectedRowIds().concat([contactMember.id]));
		const nextSelection = isChecked
			? currentSelection.filter(
					(item) => !item.contactMemberId || !ids.includes(item.contactMemberId),
				)
			: currentSelection.concat(
					ids
						.filter((id) => !currentSelection.some((item) => item.contactMemberId === id))
						.map((id) => ({
							id: generateId(),
							contactMemberId: id,
							substitutionPlanProjectItemId: substitutionPlanProjectItemId,
						})),
				);
		methods.setValue("items", nextSelection);
	}

	if (!contactMember.email) return <p>{t("misc.no_email_available")}</p>;

	/** Render */
	return (
		<div className="h-stack justify-between pr-4">
			<Switch
				checked={isChecked}
				onCheckedChange={handleOnCheckedChange}
			/>
			<div className="h-stack">
				<p>{t(isChecked ? "substitutions.notify" : "substitutions.dont_notify")}</p>
				<Icon
					name={isChecked ? "Bell" : "BellOff"}
					className={isChecked ? "stroke-emerald-500" : "stroke-primary-300"}
				/>
			</div>
		</div>
	);
}
